/**
 * Authenticated fetch wrapper with global 401 interception.
 *
 * Drop-in replacement for `fetch()` that:
 * 1. Adds Authorization header from the auth store
 * 2. On 401 response, triggers logout + redirect via Zustand state
 * 3. Passes through all other responses unchanged
 */

import { useAuth } from '@/hooks/useAuth'

/**
 * Custom error class for auth expiry — callers can check `instanceof`
 * to distinguish session-expired errors from network failures.
 */
export class AuthExpiredError extends Error {
  constructor(message = 'Authentication expired') {
    super(message)
    this.name = 'AuthExpiredError'
  }
}

/**
 * Authenticated fetch wrapper with global 401 interception.
 *
 * Usage: Replace `fetch(url, { headers: { ...getAuthHeader(), ... } })`
 * with    `fetchWithAuth(url, { headers: { ... } })`
 *
 * - Automatically injects `Authorization: Bearer <token>`
 * - On 401 → triggers `logoutWithReason('session_401')` and throws `AuthExpiredError`
 * - Does NOT throw on non-401 HTTP errors — callers handle those as before
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const token = useAuth.getState().token

  const headers = new Headers(init?.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(input, { ...init, headers })

  if (response.status === 401) {
    useAuth.getState().logoutWithReason('session_401')
    throw new AuthExpiredError('Session expired')
  }

  return response
}
